import React, { useContext, useEffect, useRef, useState } from 'react';
import './WorkOrderForm.css';
import '../common/Button.css';
import Tooltip from '../common/Tooltip';
import { createWorkOrder } from '../../api';
import { MaterialTypesContext } from '../../context/MaterialTypesContext';

import { machineOptionKey, parseMachineOptionKey } from './workOrderLifecycle';

function WorkOrderForm({ initialData = {}, onClose }) {
  const {
    activeMaterialTypes = [], activeMaterialShapes = [],
    materialCatalogLoading = false, materialCatalogError = '', materialCatalogAvailable = true,
    materialTypesError = '', materialShapesError = '',
  } = useContext(MaterialTypesContext);
  const catalogError = materialCatalogError || materialTypesError || materialShapesError;
  const catalogBlocked = materialCatalogLoading || Boolean(catalogError) || !materialCatalogAvailable;
  const machineOptions = initialData.machineOptions || initialData.machineList || [];
  const [selectedMachineKey, setSelectedMachineKey] = useState(() => machineOptionKey(initialData));
  const [formData, setFormData] = useState(() => ({
    jobName: initialData.jobName || '',
    materialType: String(initialData.materialType || ''),
    materialShape: initialData.materialShape || '1',
    materialWidth: initialData.materialWidth || '',
    materialHeight: initialData.materialHeight || '',
    inputLength: initialData.workLength || initialData.inputLength || '',
    inputNum: initialData.inputNum || '',
    memo: initialData.memo || '',
    urgent: Boolean(initialData.urgent),
  }));
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const submitLockedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setFormData((prev) => {
      if (activeMaterialTypes.some((material) => String(material.code) === prev.materialType)) return prev;
      const initialMaterialType = String(initialData.materialType || '');
      const nextMaterialType = activeMaterialTypes.some((material) => String(material.code) === initialMaterialType)
        ? initialMaterialType : String(activeMaterialTypes[0]?.code || '');
      return nextMaterialType === prev.materialType ? prev : { ...prev, materialType: nextMaterialType };
    });
  }, [initialData.materialType, activeMaterialTypes]);

  useEffect(() => {
    setFormData((prev) => {
      if (activeMaterialShapes.some((shape) => String(shape.code) === String(prev.materialShape))) return prev;
      const initialMaterialShape = String(initialData.materialShape || '');
      const nextMaterialShape = activeMaterialShapes.some((shape) => String(shape.code) === initialMaterialShape)
        ? initialMaterialShape : String(activeMaterialShapes[0]?.code || '');
      return nextMaterialShape === String(prev.materialShape) ? prev : { ...prev, materialShape: nextMaterialShape };
    });
  }, [initialData.materialShape, activeMaterialShapes]);

  const handleChange = ({ target: { name, value, type, checked } }) => {
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isConfirmed || catalogBlocked || submitLockedRef.current) return;
    submitLockedRef.current = true;
    setIsPending(true);
    const identity = parseMachineOptionKey(selectedMachineKey);
    const payload = {
      jobName: formData.jobName,
      materialType: Number(formData.materialType),
      materialShape: Number(formData.materialShape),
      materialWidth: parseFloat(formData.materialWidth) || 0,
      materialHeight: parseFloat(formData.materialHeight) || 0,
      inputLength: parseFloat(formData.inputLength) || 0,
      inputNum: parseInt(formData.inputNum, 10) || 0,
      memo: formData.memo,
      urgent: formData.urgent,
      ...(identity || {}),
    };
    try {
      await createWorkOrder(payload);
      if (!mountedRef.current) return;
      onClose();
    } catch (error) {
      if (!mountedRef.current) return;
      window.alert(`작업 지시 생성 실패: ${error.message}`);
    } finally {
      submitLockedRef.current = false;
      if (mountedRef.current) setIsPending(false);
    }
  };

  const connected = machineOptions.filter((machine) => machine.machineKind === 'connected');
  const manual = machineOptions.filter((machine) => machine.machineKind === 'manual');
  const renderOptions = (items) => items.map((machine) => (
    <option key={machineOptionKey(machine)} value={machineOptionKey(machine)}>{machine.machineName}</option>
  ));

  return (
    <form className="work-order-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <h2>신규 작업 지시</h2>
        <button type="button" className="close-button" aria-label="작업지시 창 닫기" onClick={onClose} disabled={isPending}>X</button>
      </div>
      <fieldset disabled={isPending} className="work-order-form-fields">
        <p className="draft-notice">저장만 수행합니다. 장비로 전송하려면 생성 후 카드의 전송 버튼을 누르세요.</p>
        {materialCatalogLoading && <p className="draft-notice">소재 카탈로그를 불러오는 중입니다.</p>}
        {catalogError && <p className="draft-notice" role="alert">소재 카탈로그를 불러오지 못했습니다. 다시 시도해주세요.</p>}
        <div className="form-grid">
        <div className="form-group">
          <label htmlFor="work-order-machine">장비 선택</label>
          <select id="work-order-machine" value={selectedMachineKey} onChange={(event) => setSelectedMachineKey(event.target.value)}>
            <option value="">미지정</option>
            {connected.length > 0 && <optgroup label="연결 장비">{renderOptions(connected)}</optgroup>}
            {manual.length > 0 && <optgroup label="기타 장비">{renderOptions(manual)}</optgroup>}
          </select>
          {!selectedMachineKey && initialData.machineName && !initialData.machineRef && (
            <span className="legacy-machine-note">기존 장비명: {initialData.machineName} (새 식별자를 선택하세요)</span>
          )}
        </div>
        <div className="form-group"><label htmlFor="work-order-job">작업명</label><input id="work-order-job" type="text" name="jobName" value={formData.jobName} onChange={handleChange} placeholder="작업명 12글자 이하" maxLength="12" /></div>
        <div className="form-group"><label htmlFor="work-order-material">소재 종류</label><select id="work-order-material" name="materialType" value={formData.materialType} onChange={handleChange} disabled={catalogBlocked}><option value="" disabled>Select</option>{activeMaterialTypes.map((material) => <option key={material.code} value={material.code}>{material.label}</option>)}</select></div>
        <div className="form-group"><label htmlFor="work-order-shape">소재 모양</label><select id="work-order-shape" name="materialShape" value={formData.materialShape} onChange={handleChange} disabled={catalogBlocked}>{activeMaterialShapes.map((shape) => <option key={shape.code} value={shape.code}>{shape.label}</option>)}</select></div>
        <div className="form-group"><label htmlFor="work-order-width">소재 크기 (가로)</label><div className="input-with-unit"><input id="work-order-width" type="text" name="materialWidth" value={formData.materialWidth} onChange={handleChange} placeholder="0.00" /><span>mm</span></div></div>
        <div className="form-group"><label htmlFor="work-order-height">소재 크기 (세로)</label><div className="input-with-unit"><input id="work-order-height" type="text" name="materialHeight" value={formData.materialHeight} onChange={handleChange} placeholder="0.00" /><span>mm</span></div></div>
        <div className="form-group"><label htmlFor="work-order-length">입력 길이</label><div className="input-with-unit"><input id="work-order-length" type="text" name="inputLength" value={formData.inputLength} onChange={handleChange} placeholder="0.00" /><span>mm</span></div></div>
        <div className="form-group"><label htmlFor="work-order-count">입력 수량</label><div className="input-with-unit"><input id="work-order-count" type="text" name="inputNum" value={formData.inputNum} onChange={handleChange} placeholder="0" /><span>EA</span></div></div>
        <div className="form-group"><label htmlFor="work-order-memo">비고</label><input id="work-order-memo" name="memo" value={formData.memo} onChange={handleChange} maxLength="500" /></div>
        <label className="urgent-checkbox"><input type="checkbox" name="urgent" checked={formData.urgent} onChange={handleChange} />긴급 작업</label>
        </div>
        <div className="form-actions">
        <label className="confirm-checkbox-container"><input type="checkbox" checked={isConfirmed} onChange={(event) => setIsConfirmed(event.target.checked)} />입력 정보를 확인했습니다.</label>
        <button type="button" className="btn btn-secondary" onClick={onClose}>취소</button>
        <Tooltip message={!isConfirmed ? '입력 정보를 확인 후 체크 박스를 체크해주세요.' : ''}>
          <button type="submit" className="btn btn-primary" disabled={!isConfirmed || isPending || catalogBlocked}>생성</button>
        </Tooltip>
        </div>
      </fieldset>
    </form>
  );
}

export default WorkOrderForm;
